/**
 * AIInsightsPanel Component
 * AI Insights tab with health score, pre-written quick questions,
 * custom question input, and AI response area.
 * Gracefully degrades when ANTHROPIC_API_KEY is missing.
 */

import React, { useState, useCallback, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { StatusBadge } from './StatusBadge';
import { CommentButton } from './CommentButton';
import { PageNotes } from './PageNotes';
import { fetchHealthScore, queryAI } from '@/api/aiApi';
import type { HealthScoreResponse, AIQueryResponse } from '@/api/aiApi';
import './AIInsightsPanel.css';

function simpleMarkdown(text: string): string {
  return text
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/^## (.+)$/gm, '<h3>$1</h3>')
    .replace(/^### (.+)$/gm, '<h4>$1</h4>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>\n?)+/g, (m) => `<ul>${m}</ul>`)
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br>')
    .replace(/^/, '<p>').replace(/$/, '</p>');
}

const QUICK_QUESTIONS = [
  { label: 'Cash runway outlook', question: 'How long will our cash last at current burn rate?', hint: 'cash' },
  { label: 'Revenue trend', question: 'What is our revenue trend over the last 3 months?', hint: 'revenue' },
  { label: 'Biggest expenses', question: 'What are our top 5 expense categories this quarter?', hint: 'expenses' },
  { label: 'Overdue risk', question: 'Which overdue invoices pose the highest collection risk?', hint: 'general' },
  { label: 'Tax obligations', question: 'What are our upcoming tax and super obligations?', hint: 'compliance' },
  { label: 'Profitability check', question: 'What is our gross and net profit margin?', hint: 'revenue' },
];

function ratingToStatus(rating: string): 'healthy' | 'warning' | 'critical' | 'info' {
  if (rating === 'excellent' || rating === 'good') return 'healthy';
  if (rating === 'fair') return 'warning';
  if (rating === 'poor' || rating === 'critical') return 'critical';
  return 'info';
}

function scoreColor(score: number): string {
  if (score >= 80) return 'var(--success)';
  if (score >= 60) return 'var(--warning)';
  return 'var(--error)';
}

export function AIInsightsPanel() {
  const [healthScore, setHealthScore] = useState<HealthScoreResponse['data'] | null>(null);
  const [healthLoading, setHealthLoading] = useState(true);
  const [healthError, setHealthError] = useState<string | null>(null);

  const [queryResponse, setQueryResponse] = useState<AIQueryResponse['data'] | null>(null);
  const [queryLoading, setQueryLoading] = useState(false);
  const [queryError, setQueryError] = useState<string | null>(null);
  const [customQuestion, setCustomQuestion] = useState('');
  const [activeQuestion, setActiveQuestion] = useState<string | null>(null);

  // Fetch health score on mount
  useEffect(() => {
    let cancelled = false;
    async function loadHealthScore() {
      try {
        setHealthLoading(true);
        setHealthError(null);
        const data = await fetchHealthScore();
        if (!cancelled) setHealthScore(data);
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Failed to load health score';
          setHealthError(message);
        }
      } finally {
        if (!cancelled) setHealthLoading(false);
      }
    }
    loadHealthScore();
    return () => { cancelled = true; };
  }, []);

  const askQuestion = useCallback(async (question: string, hint?: string) => {
    try {
      setQueryLoading(true);
      setQueryError(null);
      setActiveQuestion(question);
      setQueryResponse(null);
      const data = await queryAI(question, hint);
      setQueryResponse(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to get AI response';
      setQueryError(message);
    } finally {
      setQueryLoading(false);
    }
  }, []);

  const handleCustomSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (customQuestion.trim()) {
        askQuestion(customQuestion.trim());
        setCustomQuestion('');
      }
    },
    [customQuestion, askQuestion]
  );

  const isUnavailable =
    healthError?.includes('API key') ||
    healthError?.includes('not configured') ||
    healthError?.includes('AI service') ||
    healthError?.includes('ANTHROPIC') ||
    healthError?.includes('503') ||
    healthError?.includes('unavailable') ||
    healthError?.includes('Unable to connect');

  return (
    <div className="ai-insights">
      <header className="ai-insights__header">
        <div>
          <h1 className="ai-insights__title">AI Insights</h1>
          <p className="ai-insights__subtitle">
            AI-powered financial analysis
          </p>
        </div>
      </header>

      {/* Graceful degradation when AI is not configured */}
      {isUnavailable && (
        <div className="ai-insights__unavailable" role="status">
          <div className="ai-insights__unavailable-icon">&#129302;</div>
          <h2 className="ai-insights__unavailable-title">AI insights not configured</h2>
          <p className="ai-insights__unavailable-message">
            Set the ANTHROPIC_API_KEY environment variable to enable AI-powered analysis.
            Your financial dashboards on other tabs are fully functional without this.
          </p>
        </div>
      )}

      {!isUnavailable && (
        <>
          {/* Health Score */}
          <section className="ai-insights__health" aria-label="Financial health score">
            <div className="ai-insights__section-header">
              <h2 className="ai-insights__section-title">Financial Health Score</h2>
              {healthScore && (
                <CommentButton targetType="metric" targetId="ai-health-score" />
              )}
            </div>
            {healthLoading && (
              <div className="ai-insights__health-loading">
                <div className="skeleton" style={{ height: '24px', width: '100%', maxWidth: '400px' }} />
                <div className="skeleton" style={{ height: '16px', width: '60%', maxWidth: '240px', marginTop: '8px' }} />
              </div>
            )}
            {healthError && !isUnavailable && (
              <p className="ai-insights__error">{healthError}</p>
            )}
            {healthScore && (
              <div className="ai-insights__health-content">
                <div className="ai-insights__score-bar">
                  <div className="ai-insights__score-track">
                    <div
                      className="ai-insights__score-fill"
                      style={{
                        width: `${healthScore.overallScore}%`,
                        backgroundColor: scoreColor(healthScore.overallScore),
                      }}
                      role="progressbar"
                      aria-valuenow={healthScore.overallScore}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-label={`Health score: ${healthScore.overallScore} out of 100`}
                    />
                  </div>
                  <div className="ai-insights__score-label">
                    <span className="ai-insights__score-number">{healthScore.overallScore}</span>
                    <span className="ai-insights__score-max">/100</span>
                    <StatusBadge
                      status={ratingToStatus(healthScore.rating)}
                      label={healthScore.rating}
                    />
                  </div>
                </div>
                <p className="ai-insights__summary">{healthScore.summary}</p>
                {healthScore.recommendations.length > 0 && (
                  <ul className="ai-insights__recommendations">
                    {healthScore.recommendations.map((rec, i) => (
                      <li key={i} className="ai-insights__recommendation">{rec}</li>
                    ))}
                  </ul>
                )}
                <p className="ai-insights__disclaimer">{healthScore.disclaimer}</p>
              </div>
            )}
          </section>

          {/* Quick Questions */}
          <section className="ai-insights__questions" aria-label="Quick questions">
            <h2 className="ai-insights__section-title">Ask a question</h2>
            <div className="ai-insights__quick-buttons">
              {QUICK_QUESTIONS.map((q) => (
                <button
                  key={q.label}
                  className={`ai-insights__quick-btn ${activeQuestion === q.question ? 'ai-insights__quick-btn--active' : ''}`}
                  onClick={() => askQuestion(q.question, q.hint)}
                  disabled={queryLoading}
                  type="button"
                >
                  {q.label}
                </button>
              ))}
            </div>

            {/* Custom Question */}
            <form className="ai-insights__custom-form" onSubmit={handleCustomSubmit}>
              <input
                type="text"
                className="ai-insights__custom-input"
                placeholder="Ask a custom question about your finances..."
                value={customQuestion}
                onChange={(e) => setCustomQuestion(e.target.value)}
                disabled={queryLoading}
                aria-label="Custom financial question"
              />
              <button
                type="submit"
                className="ai-insights__custom-submit"
                disabled={queryLoading || !customQuestion.trim()}
              >
                {queryLoading ? 'Thinking...' : 'Ask'}
              </button>
            </form>
          </section>

          {/* Response Area */}
          {(queryLoading || queryResponse || queryError) && (
            <section className="ai-insights__response" aria-label="AI response" aria-live="polite">
              {queryLoading && (
                <div className="ai-insights__response-loading">
                  <div className="loading-spinner" />
                  <p>Analyzing your financial data...</p>
                </div>
              )}
              {queryError && (
                <div className="ai-insights__response-error">
                  <p>{queryError}</p>
                </div>
              )}
              {queryResponse && !queryLoading && (
                <div className="ai-insights__response-content">
                  <div className="ai-insights__response-header">
                    <span className="ai-insights__response-question">
                      {queryResponse.question}
                    </span>
                    <StatusBadge
                      status={queryResponse.confidence === 'high' ? 'healthy' : queryResponse.confidence === 'medium' ? 'warning' : 'critical'}
                      label={`${queryResponse.confidence} confidence`}
                      size="sm"
                    />
                    <CommentButton
                      targetType="page"
                      targetId={`ai-response-${queryResponse.answeredAt}`}
                    />
                  </div>
                  <div
                    className="ai-insights__response-answer"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(simpleMarkdown(queryResponse.answer)) }}
                  />
                  {queryResponse.dataSources.length > 0 && (
                    <div className="ai-insights__response-sources">
                      <span className="ai-insights__sources-label">Data sources:</span>
                      {queryResponse.dataSources.join(', ')}
                    </div>
                  )}
                  <p className="ai-insights__disclaimer">{queryResponse.disclaimer}</p>
                </div>
              )}
            </section>
          )}
        </>
      )}

      <PageNotes page="ai-insights" title="AI Insights Notes" />
    </div>
  );
}
